/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DocumentTitle,
    Input,
    Pending,
    toInt,
    useErrorState,
} from '@finbeat/react-toolkit';
import { changeScoringSettings, getScoringSettings } from 'axios/scoringSettingsApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { t } from 'i18next';
import { ScoringSettingsDto } from 'models/api/scoringSettings';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Card, CardBody, CardHeader, Col, Container, FormGroup, Row } from 'reactstrap';
import './ScoringSettings.scss';

const ScoringSettings = () => {
    const { handleRequestError, handleError } = useErrorState();
    const { hasRole } = useAuth();
    const api = useApi();
    useTranslation();

    const [scoringSettings, setScoringSettings] = useState<ScoringSettingsDto | undefined>(
        undefined
    );

    const { isFetching } = useQuery('ScoringSettings', () => getScoringSettings(api), {
        onSuccess: (data) => {
            setScoringSettings(data);
        },
        onError: (e) => handleRequestError(e),
    });

    const updateScoringSettings = useMutation(
        'updateScoringSettings',
        (scoringSettings: ScoringSettingsDto) => changeScoringSettings(api, scoringSettings),
        {
            onError: () => handleError(),
        }
    );

    const className = 'scoring-settings';
    return (
        <DocumentTitle code="BackOfficeScoringOptions">
            {isFetching || !scoringSettings ? (
                <Pending />
            ) : (
                <Container className={className + ' mx-auto main-card'}>
                    <Row>
                        <Col>
                            <Card>
                                <CardHeader>
                                    <h4>
                                        <Trans i18nKey="BackOfficeCacheRetentionPeriod" />
                                    </h4>
                                </CardHeader>
                                <CardBody>
                                    {scoringSettings.cacheExpiry.map((item) => (
                                        <FormGroup key={item.id}>
                                            <Input
                                                id="transUnion"
                                                label={
                                                    item.serviceName +
                                                    ' ' +
                                                    t('BackOfficeCountDays')
                                                }
                                                type="number"
                                                value={item.expiry.toString()}
                                                onChange={(e) => {
                                                    item.expiry = toInt(e.target.value) ?? 1;
                                                    setScoringSettings({ ...scoringSettings });
                                                }}
                                                max="1000"
                                                min="1"
                                                onKeyDown={(e) => {
                                                    if (e.key === '.' || e.key === ',') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                invalid={
                                                    scoringSettings.cacheExpiry[0].expiry > 1000
                                                }
                                                loading={updateScoringSettings.isLoading}
                                                disabled={!hasRole('scoringSettingsUpdate')}
                                            />
                                        </FormGroup>
                                    ))}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="px-0 pt-3">
                                <Button
                                    className={`${className}__save`}
                                    color="primary"
                                    disabled={
                                        updateScoringSettings.isLoading ||
                                        !hasRole('scoringSettingsUpdate')
                                    }
                                    isPending={updateScoringSettings.isLoading}
                                    onClick={() =>
                                        scoringSettings &&
                                        updateScoringSettings.mutate(scoringSettings)
                                    }
                                >
                                    <Trans i18nKey="BackOfficeSave" />
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            )}
        </DocumentTitle>
    );
};

export default ScoringSettings;
